import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import './web-test-payout.css'
import { RISK_LINKS, STRATEGY_RISK_DISCLOSURE } from './risk-copy'
import { trackStrategyViewed } from '../analytics/track'

type PayoutExample = {
  id: string
  label: string
  stake: string
  target: string
  stop: string
  potentialReturn: string
}

/** Illustrative examples only — config-driven strategy cards */
const PAYOUT_EXAMPLES: PayoutExample[] = [
  {
    id: 'safe',
    label: 'SAFE',
    stake: '$100',
    target: '$5',
    stop: '$5',
    potentialReturn: '$160',
  },
  {
    id: 'balance',
    label: 'BALANCE',
    stake: '$100',
    target: '$10',
    stop: '$5',
    potentialReturn: '$250',
  },
  {
    id: 'aggressive',
    label: 'AGGRESSIVE',
    stake: '$100',
    target: '$20',
    stop: '$5',
    potentialReturn: '$400',
  },
]

function PayoutCard({ example, index }: { example: PayoutExample; index: number }) {
  return (
    <article className="wp-card" style={{ ['--wp-i' as string]: index }}>
      <h3 className="wp-card__label">{example.label}</h3>

      <dl className="wp-rows">
        <div className="wp-row">
          <dt>Target</dt>
          <dd>{example.target}</dd>
        </div>
        <div className="wp-row">
          <dt>Stop</dt>
          <dd>{example.stop}</dd>
        </div>
        <div className="wp-row wp-row--accent">
          <dt>Illustrative total payout on {example.stake} stake</dt>
          <dd>{example.potentialReturn}</dd>
        </div>
      </dl>

      <p className="wp-card__disclaimer">Illustrative Example</p>
    </article>
  )
}

export function WebTestPayoutExamples() {
  const rootRef = useRef<HTMLElement>(null)
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    const el = rootRef.current
    if (!el) return
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setVisible(true)
          trackStrategyViewed('balance', 'strategy_section')
          io.disconnect()
        }
      },
      { threshold: 0.12 },
    )
    io.observe(el)
    return () => io.disconnect()
  }, [])

  return (
    <section
      ref={rootRef}
      id="strategies"
      className={`wp-section${visible ? ' is-visible' : ''}`}
      aria-labelledby="wp-title"
    >
      <div className="wp-inner">
        <div id="payout-examples" />
        <header className="wp-intro">
          <h2 id="wp-title" className="wp-title">
            Strategies and Illustrative Payouts
          </h2>
          <p className="wp-subtitle">
            Three strategy profiles with example target, stop and payout values.
          </p>
        </header>

        <div className="wp-grid">
          {PAYOUT_EXAMPLES.map((example, index) => (
            <PayoutCard key={example.id} example={example} index={index} />
          ))}
        </div>

        <p className="wp-disclaimer">
          Kindly note that the TP / SL will be calculated and placed automatically based on the
          entry price and trade direction.
        </p>

        <div className="wp-risk-box" role="note">
          <p className="wp-risk-box__text">{STRATEGY_RISK_DISCLOSURE}</p>
          <p className="wp-risk-box__link">
            <Link to={RISK_LINKS.disclosure}>Read the full Risk Disclosure</Link>
          </p>
        </div>
      </div>
    </section>
  )
}
